import {UseQueryOptions} from "@tanstack/react-query/src/types";
import {useQuery} from "@tanstack/react-query";
import {CausaleWFRecord} from "@commons/mapping/CausaleWFRecord";
import {CausaliWFParams, getImpiantoWF, listCausaliWF, TabellaDominioParams} from "@commons/services/presenze_api/workflow.api";
import {TabellaWFRecord} from "@commons/mapping/TabellaWFRecord";
import {useEffect, useState} from "react";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda} from "@commons/core.slice";
import {listRichiesteWFPeriodo} from "@commons/services/presenze_calcolo/GestionePlus.api";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {Periodo} from "@commons/types";
import {FPNetDate} from "@commons/types";
import {DateTime} from "luxon";
import {PresenzeLavoratoreParams} from "@commons/services/lavoratori/lavoratori.presenze.api";
import {MonitorWFData} from "@commons/services/presenze_api/monitorWF.api";

// TABELLE_WF
export function useTabellaWF(params: Partial<TabellaDominioParams> | null, opt?: UseQueryOptions) {
    return useQuery<TabellaWFRecord>({
        queryKey: ["tabellaWF", params],
        queryFn: () => {
            return getImpiantoWF(params!)
        },
        enabled: !!params && !!params.idDominio && !!params.idTabella,
    });
}

// TABELLE_WF_CAUSALI
/**
 * N.B. Solo uso "interno" (comboBox), usare invece useCausaliWF
 * @param params
 * @param opt
 */
export function useCustomCausaliWF(params: CausaliWFParams | null, opt?: UseQueryOptions) {
    const enabled = (!!params && !!params.idDominio && !!params.idTabella && !!params.idAziendaTabelle);
    return {
        ...useQuery<CausaleWFRecord[]>({
            queryKey: ["causaliWF", params],
            queryFn: () => {
                return listCausaliWF(params!)
            },
            enabled,
        }), enabled
    };
}

export function useCausaliWF() {
    const azienda = useAppSelector(selectAzienda); // Azienda corrente
    const [params, setNextParams] = useState<CausaliWFParams | null>(null);

    // Al variare dell'azienda imposto i parametri per il caricamento dell'elenco delle causali di WF
    useEffect(() => {
        if (azienda) {
            setNextParams({
                view: 'Full',
                idDominio: azienda.idDominio!,
                idTabella: azienda.idTabellaWF,
                idAziendaTabelle: azienda.idAziendaCDIC,
            } as CausaliWFParams);
        }
    }, [azienda]);
    return {...useCustomCausaliWF(params), params};
}

function usePresenzeCausaliWF(params: PresenzeLavoratoreParams | null, opt?: UseQueryOptions) {
    return useQuery<MonitorWFData>({
        queryKey: ["presenzeCausaliWF", params],
        queryFn: () => {
            return listRichiesteWFPeriodo(params!, true)
        },
        enabled: !!params && !!params.idDip && !!params.anno && !!params.mese,
    });
}

/**
 * usePresenzeCausaliWF preleva tutte le richieste effettuate dal lavoratore indicato e per il periodo indicato
 * N.B. per periodo si intendono le giornate dell'anno/mese indicato e quelle nelle settimane a cavallo con il mese precedente e quello successivo
 * @param lavoratore
 * @param periodo
 */
export const usePresenzeCausali = (lavoratore: LavoratorePresenzeRecord | undefined, periodo: Periodo, decorrenza: FPNetDate, fineValidita: FPNetDate) => {
    const minDate = decorrenza ? decorrenza : DateTime.now().minus({years: 1}).toFormat('yyyy-LL-dd');
    const maxDate = fineValidita ? fineValidita : DateTime.now().plus({years: 1}).toFormat('yyyy-LL-dd');
    const periodoIsValid = Periodo.isBetween(periodo, minDate, maxDate);
    let paramsRichiesteWF;
    if (lavoratore && periodoIsValid) {
        paramsRichiesteWF = {
            idAzienda: lavoratore.idAzienda,
            idAziendaMaster: lavoratore.idAziendaMaster,
            idDip: lavoratore.id,
            anno: periodo.anno,
            mese: periodo.mese,
            extraInfo: false,
            rilevaPianificazione: false,
        };
    }

    // hook per GET richieste di WF del lavoratore corrente
    const {data: richiesteWF, refetch: refetchRichiesteWF} = usePresenzeCausaliWF(paramsRichiesteWF || null);
    return {richiesteWF, refetchRichiesteWF};
};
